const DEFAULT_THEME = {
  primary: '#12385b',
  secondary: '#0f766e',
  accent: '#f4b942',
  background: '#f8fafc',
  font: '#ffffff',
  fontSecondary: '#ffffff',
  fontAccent: '#172033'
};

const clamp01 = (value) => Math.min(Math.max(value, 0), 1);

const normaliseHex = (value, fallback) => {
  if (typeof value !== 'string') {
    return fallback;
  }
  let hex = value.trim();
  if (!hex) {
    return fallback;
  }
  if (!hex.startsWith('#')) {
    hex = `#${hex}`;
  }

  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex);
  if (!match) {
    return fallback;
  }

  if (match[1].length === 3) {
    const [r, g, b] = match[1];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  return `#${match[1].toLowerCase()}`;
};

const hexToRgb = (hex) => {
  const value = normaliseHex(hex, '#000000').substring(1);
  const bigint = parseInt(value, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255
  };
};

const rgbToHex = (r, g, b) => {
  const toHex = (component) => component.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const rgbToHsl = ({ r, g, b }) => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === rn) {
      h = ((gn - bn) / delta) % 6;
    } else if (max === gn) {
      h = (bn - rn) / delta + 2;
    } else {
      h = (rn - gn) / delta + 4;
    }
  }

  h *= 60;
  if (h < 0) {
    h += 360;
  }

  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  return { h, s, l };
};

const hslToRgb = ({ h, s, l }) => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let rn = 0;
  let gn = 0;
  let bn = 0;

  if (h >= 0 && h < 60) {
    rn = c;
    gn = x;
  } else if (h >= 60 && h < 120) {
    rn = x;
    gn = c;
  } else if (h >= 120 && h < 180) {
    gn = c;
    bn = x;
  } else if (h >= 180 && h < 240) {
    gn = x;
    bn = c;
  } else if (h >= 240 && h < 300) {
    rn = x;
    bn = c;
  } else {
    rn = c;
    bn = x;
  }

  return {
    r: Math.round((rn + m) * 255),
    g: Math.round((gn + m) * 255),
    b: Math.round((bn + m) * 255)
  };
};

const adjustLightness = (hex, delta) => {
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb);
  hsl.l = clamp01(hsl.l + delta);
  const adjusted = hslToRgb(hsl);
  return rgbToHex(adjusted.r, adjusted.g, adjusted.b);
};

const mixColors = (foreground, background, weight = 0.5) => {
  const fg = hexToRgb(foreground);
  const bg = hexToRgb(background);
  const w = clamp01(weight);
  const r = Math.round(fg.r * (1 - w) + bg.r * w);
  const g = Math.round(fg.g * (1 - w) + bg.g * w);
  const b = Math.round(fg.b * (1 - w) + bg.b * w);
  return rgbToHex(r, g, b);
};

const getLuminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const transform = (value) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const rl = transform(r);
  const gl = transform(g);
  const bl = transform(b);
  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
};

const isColorLight = (hex) => getLuminance(hex) > 0.6;

const contrastRatio = (a, b) => {
  const values = [getLuminance(a), getLuminance(b)].sort((x, y) => y - x);
  return (values[0] + 0.05) / (values[1] + 0.05);
};
const getContrastColor = (hex) => contrastRatio(hex, '#ffffff') >= contrastRatio(hex, '#000000') ? '#ffffff' : '#000000';
const readableColor = (preferred, background) => contrastRatio(preferred, background) >= 4.5 ? preferred : getContrastColor(background);

const ensureThemeContrast = (inputTheme = null) => {
  const source = { ...DEFAULT_THEME, ...(inputTheme || {}) };
  const primary = normaliseHex(source.primary, DEFAULT_THEME.primary);
  const secondary = normaliseHex(source.secondary, DEFAULT_THEME.secondary);
  const accent = normaliseHex(source.accent, DEFAULT_THEME.accent);
  const background = normaliseHex(source.background, DEFAULT_THEME.background);
  return {
    primary,
    secondary,
    accent,
    background,
    font: readableColor(normaliseHex(source.font || source.text || source.onPrimary, DEFAULT_THEME.font), primary),
    fontSecondary: readableColor(normaliseHex(source.fontSecondary || source.secondaryFont || source.onSecondary, DEFAULT_THEME.fontSecondary), secondary),
    fontAccent: readableColor(normaliseHex(source.fontAccent || source.accentFont || source.onAccent, DEFAULT_THEME.fontAccent), accent)
  };
};

const formatRgbVar = ({ r, g, b }) => `${r} ${g} ${b}`;

const buildThemePalette = (inputTheme = null) => {
  const source = {
    ...DEFAULT_THEME,
    ...(inputTheme || {})
  };

  const accessibleTheme = ensureThemeContrast(source);
  const { primary, secondary, accent, background } = accessibleTheme;

  const onPrimary = accessibleTheme.font;
  const onSecondary = accessibleTheme.fontSecondary;
  const onAccent = accessibleTheme.fontAccent;
  const onBackground = readableColor(normaliseHex(source.font, DEFAULT_THEME.font), background);

  const primaryIsLight = isColorLight(primary);
  const backgroundIsLight = isColorLight(background);

  const primaryHover = adjustLightness(primary, primaryIsLight ? -0.12 : 0.12);
  const primaryBorder = adjustLightness(primary, primaryIsLight ? -0.25 : 0.18);
  const primaryTint = mixColors(onPrimary, primary, 0.75);

  const secondaryHover = adjustLightness(secondary, isColorLight(secondary) ? -0.12 : 0.12);
  const secondaryBorder = adjustLightness(secondary, isColorLight(secondary) ? -0.25 : 0.18);

  const accentHover = adjustLightness(accent, isColorLight(accent) ? -0.12 : 0.12);
  const accentBorder = adjustLightness(accent, isColorLight(accent) ? -0.25 : 0.18);
  const accentTint = mixColors(onAccent, accent, 0.7);

  const surface = mixColors(background, backgroundIsLight ? '#ffffff' : '#111827', 0.75);
  const surfaceBorder = mixColors(onBackground, background, 0.82);
  const muted = readableColor(mixColors(onBackground, background, 0.28), surface);

  const palette = {
    primary,
    secondary,
    accent,
    background,
    font: onPrimary,
    fontSecondary: onSecondary,
    fontAccent: onAccent,
    onPrimary,
    onSecondary,
    onAccent,
    onBackground,
    primaryHover,
    primaryBorder,
    secondaryHover,
    secondaryBorder,
    accentHover,
    accentBorder,
    surface,
    surfaceBorder,
    muted,
    primaryTint,
    accentTint,
    primaryIsLight,
    backgroundIsLight,
    primaryRgb: hexToRgb(primary),
    secondaryRgb: hexToRgb(secondary),
    accentRgb: hexToRgb(accent),
    onPrimaryRgb: hexToRgb(onPrimary),
    onSecondaryRgb: hexToRgb(onSecondary),
    onAccentRgb: hexToRgb(onAccent),
    onBackgroundRgb: hexToRgb(onBackground)
  };

  const cssVariables = {
    '--shop-link': readableColor(primary, surface),
    '--shop-secondary-link': readableColor(secondary, surface),
    '--shop-primary-hover-text': readableColor(onPrimary, primaryHover),
    '--shop-secondary-hover-text': readableColor(onSecondary, secondaryHover),
    '--shop-primary': palette.primary,
    '--shop-secondary': palette.secondary,
    '--shop-accent': palette.accent,
    '--shop-background': palette.background,
    '--shop-surface': palette.surface,
    '--shop-surface-border': palette.surfaceBorder,
    '--shop-font': palette.font,
    '--shop-on-primary': palette.onPrimary,
    '--shop-on-secondary': palette.onSecondary,
    '--shop-on-accent': palette.onAccent,
    '--shop-on-background': palette.onBackground,
    '--shop-font-secondary': palette.fontSecondary,
    '--shop-font-accent': palette.fontAccent,
    '--shop-muted': palette.muted,
    '--shop-primary-hover': palette.primaryHover,
    '--shop-primary-border': palette.primaryBorder,
    '--shop-secondary-hover': palette.secondaryHover,
    '--shop-secondary-border': palette.secondaryBorder,
    '--shop-accent-hover': palette.accentHover,
    '--shop-accent-border': palette.accentBorder,
    '--shop-primary-tint': palette.primaryTint,
    '--shop-accent-tint': palette.accentTint,
    '--shop-primary-rgb': formatRgbVar(palette.primaryRgb),
    '--shop-secondary-rgb': formatRgbVar(palette.secondaryRgb),
    '--shop-accent-rgb': formatRgbVar(palette.accentRgb),
    '--shop-on-primary-rgb': formatRgbVar(palette.onPrimaryRgb),
    '--shop-on-secondary-rgb': formatRgbVar(palette.onSecondaryRgb),
    '--shop-on-accent-rgb': formatRgbVar(palette.onAccentRgb),
    '--shop-on-background-rgb': formatRgbVar(palette.onBackgroundRgb)
  };

  return {
    palette,
    cssVariables
  };
};

const SHOP_THEME_VARIABLE_KEYS = Object.keys(buildThemePalette().cssVariables);

export { DEFAULT_THEME, buildThemePalette, contrastRatio, ensureThemeContrast, getContrastColor, readableColor, SHOP_THEME_VARIABLE_KEYS };
